/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getInstitutions } from 'app/entities/institution/institution.reducer';
import { getEntities as getAccountingCurrencies } from 'app/entities/accounting-currency/accounting-currency.reducer';
import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getAccountingTaxes } from 'app/entities/accounting-tax/accounting-tax.reducer';
import { AddedByType } from 'app/shared/model/enumerations/added-by-type.model';
import { createEntity, getEntity, reset, updateEntity } from './accounting-bank-account.reducer';

export const AccountingBankAccountUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const institutions = useAppSelector(state => state.institution.entities);
  const accountingCurrencies = useAppSelector(state => state.accountingCurrency.entities);
  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const accountingTaxes = useAppSelector(state => state.accountingTax.entities);
  const accountingBankAccountEntity = useAppSelector(state => state.accountingBankAccount.entity);
  const loading = useAppSelector(state => state.accountingBankAccount.loading);
  const updating = useAppSelector(state => state.accountingBankAccount.updating);
  const updateSuccess = useAppSelector(state => state.accountingBankAccount.updateSuccess);
  const addedByTypeValues = Object.keys(AddedByType);

  const handleClose = () => {
    navigate(`/accounting-bank-account${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getInstitutions({}));
    dispatch(getAccountingCurrencies({}));
    dispatch(getClinicalDictionaries({}));
    dispatch(getAccountingTaxes({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.allowedCredit !== undefined && typeof values.allowedCredit !== 'number') {
      values.allowedCredit = Number(values.allowedCredit);
    }
    if (values.maxCreditLimit !== undefined && typeof values.maxCreditLimit !== 'number') {
      values.maxCreditLimit = Number(values.maxCreditLimit);
    }
    if (values.standardCreditLimit !== undefined && typeof values.standardCreditLimit !== 'number') {
      values.standardCreditLimit = Number(values.standardCreditLimit);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.openingBalance !== undefined && typeof values.openingBalance !== 'number') {
      values.openingBalance = Number(values.openingBalance);
    }
    if (values.closingBalance !== undefined && typeof values.closingBalance !== 'number') {
      values.closingBalance = Number(values.closingBalance);
    }

    const entity = {
      ...accountingBankAccountEntity,
      ...values,
      institution: institutions.find(it => it.id.toString() === values.institution?.toString()),
      baseCurrency: accountingCurrencies.find(it => it.id.toString() === values.baseCurrency?.toString()),
      accountType: clinicalDictionaries.find(it => it.id.toString() === values.accountType?.toString()),
      entityType: clinicalDictionaries.find(it => it.id.toString() === values.entityType?.toString()),
      taxes: mapIdList(values.taxes),
      otherCurrencies: mapIdList(values.otherCurrencies),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          createdByType: 'ACCOUNTANT',
          ...accountingBankAccountEntity,
          institution: accountingBankAccountEntity?.institution?.id,
          baseCurrency: accountingBankAccountEntity?.baseCurrency?.id,
          accountType: accountingBankAccountEntity?.accountType?.id,
          entityType: accountingBankAccountEntity?.entityType?.id,
          taxes: accountingBankAccountEntity?.taxes?.map(e => e.id.toString()),
          otherCurrencies: accountingBankAccountEntity?.otherCurrencies?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.accountingBankAccount.home.createOrEditLabel" data-cy="AccountingBankAccountCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.accountingBankAccount.home.createOrEditLabel">
              Create or edit a AccountingBankAccount
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="accounting-bank-account-id"
                  label={translate('tinyMedicRestApp.accountingBankAccount.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.accountHolderName')}
                id="accounting-bank-account-accountHolderName"
                name="accountHolderName"
                data-cy="accountHolderName"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.accountNo')}
                id="accounting-bank-account-accountNo"
                name="accountNo"
                data-cy="accountNo"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.ifscCode')}
                id="accounting-bank-account-ifscCode"
                name="ifscCode"
                data-cy="ifscCode"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.allowedCredit')}
                id="accounting-bank-account-allowedCredit"
                name="allowedCredit"
                data-cy="allowedCredit"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.maxCreditLimit')}
                id="accounting-bank-account-maxCreditLimit"
                name="maxCreditLimit"
                data-cy="maxCreditLimit"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.standardCreditLimit')}
                id="accounting-bank-account-standardCreditLimit"
                name="standardCreditLimit"
                data-cy="standardCreditLimit"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.inactive')}
                id="accounting-bank-account-inactive"
                name="inactive"
                data-cy="inactive"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.active')}
                id="accounting-bank-account-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.microSec')}
                id="accounting-bank-account-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.timestampEntry')}
                id="accounting-bank-account-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.lastUpdated')}
                id="accounting-bank-account-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.cursor')}
                id="accounting-bank-account-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.entityId')}
                id="accounting-bank-account-entityId"
                name="entityId"
                data-cy="entityId"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.openingBalance')}
                id="accounting-bank-account-openingBalance"
                name="openingBalance"
                data-cy="openingBalance"
                type="text"
              />
              <UncontrolledTooltip target="openingBalanceLabel">
                <Translate contentKey="tinyMedicRestApp.accountingBankAccount.help.openingBalance" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.overDraft')}
                id="accounting-bank-account-overDraft"
                name="overDraft"
                data-cy="overDraft"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="overDraftLabel">
                <Translate contentKey="tinyMedicRestApp.accountingBankAccount.help.overDraft" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.closingBalance')}
                id="accounting-bank-account-closingBalance"
                name="closingBalance"
                data-cy="closingBalance"
                type="text"
              />
              <UncontrolledTooltip target="closingBalanceLabel">
                <Translate contentKey="tinyMedicRestApp.accountingBankAccount.help.closingBalance" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.createdById')}
                id="accounting-bank-account-createdById"
                name="createdById"
                data-cy="createdById"
                type="text"
              />
              <UncontrolledTooltip target="createdByIdLabel">
                <Translate contentKey="tinyMedicRestApp.accountingBankAccount.help.createdById" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.createdByType')}
                id="accounting-bank-account-createdByType"
                name="createdByType"
                data-cy="createdByType"
                type="select"
              >
                {addedByTypeValues.map(addedByType => (
                  <option value={addedByType} key={addedByType}>
                    {translate(`tinyMedicRestApp.AddedByType.${addedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <UncontrolledTooltip target="createdByTypeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingBankAccount.help.createdByType" />
              </UncontrolledTooltip>
              <ValidatedField
                id="accounting-bank-account-institution"
                name="institution"
                data-cy="institution"
                label={translate('tinyMedicRestApp.accountingBankAccount.institution')}
                type="select"
              >
                <option value="" key="0" />
                {institutions
                  ? institutions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-bank-account-baseCurrency"
                name="baseCurrency"
                data-cy="baseCurrency"
                label={translate('tinyMedicRestApp.accountingBankAccount.baseCurrency')}
                type="select"
              >
                <option value="" key="0" />
                {accountingCurrencies
                  ? accountingCurrencies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-bank-account-accountType"
                name="accountType"
                data-cy="accountType"
                label={translate('tinyMedicRestApp.accountingBankAccount.accountType')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-bank-account-entityType"
                name="entityType"
                data-cy="entityType"
                label={translate('tinyMedicRestApp.accountingBankAccount.entityType')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.tax')}
                id="accounting-bank-account-tax"
                data-cy="tax"
                type="select"
                multiple
                name="taxes"
              >
                <option value="" key="0" />
                {accountingTaxes
                  ? accountingTaxes.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingBankAccount.otherCurrency')}
                id="accounting-bank-account-otherCurrency"
                data-cy="otherCurrency"
                type="select"
                multiple
                name="otherCurrencies"
              >
                <option value="" key="0" />
                {accountingCurrencies
                  ? accountingCurrencies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/accounting-bank-account" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AccountingBankAccountUpdate;
